/**
 * @file chat module
 * @author malcolmmzma
 */

import { Button, Tooltip } from '@tencent/spaui';
import AdqIcon from '@tencent/adq-icons';
import {
  UseChatApiProps,
  ChatServiceRefValue,
  ChatService,
  ChatServiceProps,
  ChatInputProps,
  CodePanelProps,
  MdRenderProps,
  MdCodePreviewBtn,
  MdLoading,
} from '@tencent/ai-chat-react';
import { useMemoizedFn, useLocalStorageState } from 'ahooks';
import React, { forwardRef, useImperativeHandle, useRef, useMemo, useState, useEffect } from 'react';
import _ from 'lodash';
import qs from 'qs';
import renderWorkerContent from '@tencent/ai-chat-react/worker_bundle/markdown.worker.global.js?raw';
import { Flex } from '@tencent/odc';

import reportHub from '@/common/report-hub';
import globalStore from '@/stores';

import { uploadFile, reportException } from '../../../api/analysis';
import { COPYRIGHT_TEXT, ICONS } from '../constants/ui';
import { HAS_FEEDBACK, SEND_USER_PROMPT } from '../constants/report';
import useAgentFeedback from '../components/FeedbackCard/useAgentFeedback';
import { QrPreview } from '../components/QrPreview';
import { AGENT_MAP } from '../constants/agent';
import PromptOptimize from '../components/PromptOptimize';

import type { WorkerConstructor } from '@tencent/ai-chat-react/lib/components/MdRender/types';

import '../index.less';


class RenderWorker extends Worker {
  constructor() {
    const blob = new Blob([renderWorkerContent], { type: 'application/javascript' });
    super(URL.createObjectURL(blob));
  }
}

const {
  // 开启流式代码输出面板
  code_stream_visualize: codeStreamVisualize,
  // 开启报表分析入口: woa权限&cm免登陆才能走通流程，没风险
  enable_report_analyze: enableReportAnalyze,
  // 显示真实agentName
  show_real_agent_name: showRealAgentName,
} = qs.parse(window.location.search.slice(1)) as Record<string, any>;

const renderCodeMdCodePreviewBtn: MdRenderProps['renderCodeMdCodePreviewBtn'] = (option) => {
  const { status, defaultNode, openCodePanel } = option;
  // 开启流式过程，可见所有状态
  if (codeStreamVisualize) {
    return defaultNode;
  }

  // 取消状态展示re-ask-toolbar
  if (status === 'canceled') {
    return <></>;
  }

  if (status === 'code_finish') {
    return (
      <MdCodePreviewBtn onOpenCodePanel={openCodePanel}>
        预览数据看板<AdqIcon name="right" />
      </MdCodePreviewBtn>
    );
  }

  if (status === 'code_printing') {
    return (
      <Flex
        inline
        className="tw-mt-[8px] tw-bg-blue-01-a tw-text-coolgray-12 tw-px-[16px] tw-py-[8px] tw-rounded-full"
        itemsCenter
        gap={4}
      >
        数据看板生成中，需等待1~2分钟<MdLoading size={4} />
      </Flex>
    );
  }

  return defaultNode;
};

export type ChatEngine = ChatServiceRefValue;

export interface ChatInstanceProps extends Partial<ChatServiceProps> {
  initialPrompt?: string;
  chatSessionId?: string;
  fileUrlList?: string[];
  modelId?: keyof typeof AGENT_MAP;
  agentId?: number | null;
}

// 获取chat api配置
const getOpenApiConfig = (agentId: number): UseChatApiProps['openApiConfig'] => ({
  type: 'agent_management' as const,
  url: '/sso_cgi/eden/chatStream',
  params: {
    agent_id: agentId,
  },
  headers: {
    'x-platform-type': 2,
    'x-account-id': globalStore.account.bm_id || globalStore.account.agency_uid,
  },
});

// 获取chat continue api配置
const getChatConfinueApiConfig = (agentId?: number | null, sessionId?: string | null) => {
  if (_.isNil(agentId) || _.isNil(sessionId)) {
    return;
  }

  return {
    type: 'agent_management' as const,
    url: '/sso_cgi/eden/chat_continue',
    params: {
      agent_id: agentId,
      session_id: sessionId,
    },
    headers: {
      'x-platform-type': 2,
      'x-account-id': globalStore.account.bm_id || globalStore.account.agency_uid,
    },
  };
};

/** 将 fileUrlList 转为 SDK ask 所需的 file_list 格式 */
const buildInitialFileList = (fileUrlList?: string[]) => _.map(fileUrlList || [], (url) => ({ type: 'image_url' as const, file_name: '', url: url || '' }));

const ChatInstance = forwardRef<ChatEngine, ChatInstanceProps>((props, ref) => {
  const {
    initialPrompt,
    fileUrlList,
    chatSessionId: chatSessionIdFromProps,
    modelId = 'api',
    agentId,
    onChatSessionIdChange,
    ...restProps
  } = props;

  const { account } = globalStore;

  const chatEngineRef = useRef<ChatServiceRefValue | null>(null);

  const onError: UseChatApiProps['onError'] = (error) => {
    console.error('=== 错误信息', error);
  };

  useImperativeHandle(ref, () => chatEngineRef.current!, [chatEngineRef]);

  const openApiConfig = useMemo(() => {
    const config = agentId ? getOpenApiConfig(agentId) : AGENT_MAP[modelId]?.openApiConfig;
    return {
      ...config || {},
      headers: {
        'x-platform-type': 2,
        'x-account-id': account.bm_id || account.agency_uid,
      },
    };
  }, [agentId, modelId, account]);

  const [chatSessionId, setChatSessionId] = useState(chatSessionIdFromProps);
  /** 保存 SDK ask 方法，用于从 Home 进入时等后端 sessionId 后再发首条消息 */
  const askRef = useRef<((opt: { prompt: string; file_list?: any[] }) => void) | null>(null);
  /** 防止首条 ask 重复发送 */
  const initialAskDoneRef = useRef(false);

  useEffect(() => {
    if (chatSessionIdFromProps) {
      setChatSessionId(chatSessionIdFromProps);
    }
  }, [chatSessionIdFromProps]);

  /**
   * 从 Home 进入时：先无 chatSessionId，等父组件拿到后端 sessionId 后再发首条消息，
   * 保证 chatStream 使用后端 sessionId。setTimeout(0) 确保在 SDK useDeepCompareEffect 更新 singleton.sessionId 之后再 ask。
   */
  useEffect(() => {
    if (!chatSessionIdFromProps || !initialPrompt || initialAskDoneRef.current || !askRef.current) return;
    initialAskDoneRef.current = true;
    const payload = { prompt: initialPrompt, file_list: buildInitialFileList(fileUrlList) };
    setTimeout(() => askRef.current?.(payload), 0);
  }, [chatSessionIdFromProps, initialPrompt, fileUrlList]);

  // 图片上传
  const uploadImage = useMemoizedFn<Required<Required<ChatInputProps>['uploadOptions']>['uploadAction']>(async (file) => {
    if (file.fileFrom !== 'local') {
      return;
    }

    const res = await uploadFile({ file: file.file });
    const { code, data, message: errMsg } = res || {};
    if (code !== 0) {
      throw new Error(errMsg);
    }

    return data;
  });

  const { trigger: triggerFeedback, feedbackWidget } = useAgentFeedback({
    productName: 'SeeNow (分析 Agent)',
    agentId: _.get(openApiConfig, 'params.agent_id', 0),
    sessionId: chatSessionId,
  });


  const [hasFeedback, setHasFeedback] = useLocalStorageState<boolean>(HAS_FEEDBACK, {
    defaultValue: false,
  });
  const onEnd: UseChatApiProps['onEnd'] = (_renderItem) => {
    console.log('==== 回答结束了');
    !hasFeedback && triggerFeedback(); // 自动触发反馈
    setHasFeedback(true);
  };

  const renderChatHeader = useMemoizedFn<Required<ChatServiceProps>['renderHeader']>((props) => {
    const { fold } = props;
    const sessionIdNode = (
      <div className="tw-px-[24px] tw-pb-[12px] tw-text-gray-08 tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-text-right">
        session_id: {chatSessionId || '-'}
      </div>
    );
    if (!fold) {
      return sessionIdNode;
    }

    return <div className="tw-px-[24px] tw-flex tw-items-center">{fold && sessionIdNode}</div>;
  });

  // 异常上报
  const onCodeRenderError = useMemoizedFn<Required<CodePanelProps>['onCodeRenderError']>(async (errData) => {
    const {
      session_id: sessionId,
      preview_id: previewId,
      code_id: codeId,
      chat_item_idx: chatItemIdx,
      data,
    } = errData || {};
    const { asset_url: assetUrl, error_type: errorType, col_no: colNo, row_no: rowNo, message } = data || {};

    await reportException({
      agent_id: _.get(openApiConfig, 'params.agent_id', -1),
      session_id: sessionId,
      preview_id: previewId,
      code_id: `${chatItemIdx}_${codeId}`,
      asset_url: assetUrl,
      error_type: _.snakeCase(errorType).toUpperCase(),
      col_no: colNo,
      row_no: rowNo,
      message,
    });
  });

  useEffect(() => () => {
    // 从当前会话切出，停止stream
    if (chatEngineRef?.current) {
      chatEngineRef?.current?.stop?.();
    }
  }, []);

  return (
    <>
      <ChatService
        {...restProps}
        className="ai-agent-chat-service"
        RenderWorker={RenderWorker as WorkerConstructor}
        avatarConfig={{
          user: false,
          assistant: false,
        }}
        onSend={({ prompt }) => {
          reportHub.reportAction({
            action: SEND_USER_PROMPT,
            prompt,
            session_id: chatSessionId,
          });
        }}
        onError={onError}
        onEnd={onEnd}
        ref={chatEngineRef}
        startupPageConfig={{
          tip: 'Hi, 我是服务商系统分析Agent，你想分析什么数据？',
        }}
        messageOptions={{
          attach: () => chatEngineRef.current?.chatRef?.elChatRoot || document.body,
        }}
        presetPrompt={{
          autoAsk: false,
          // 预置问题
          list: [],
        }}
        chatSessionId={chatSessionId}
        onChatSessionIdChange={(val: string) => {
          onChatSessionIdChange?.(val);
          setChatSessionId(val);
        }}
        renderHeader={renderChatHeader}
        inputConfig={{
          copyright: COPYRIGHT_TEXT,
          uploadOptions: {
            enabled: true,
            uploadAction: uploadImage,
          },
          bottomComponents: {
            left: [
              <PromptOptimize
                key="prompt-optimize"
                getPrompt={chatEngineRef.current?.chatRef?.getPrompt}
                onOptimize={(optimizedPrompt) => {
                  chatEngineRef.current?.chatRef?.setPrompt?.(optimizedPrompt);
                }}
              />,
            ],
          },
        }}
        chatItemProps={{
          renderAgentName: showRealAgentName
            ? undefined
            : (option) => (!!_.get(option, 'chatContent.content') || !!_.get(option, 'chatContent.reasoning_data.reasoning_content')) && (
              <div className="tw-pb-[12px]" style={{ fontSize: 0 }}>
                <img className="tw-h-[20px] tw-pointer-events-none" src={ICONS.AGENT_NAME_ICON} />
              </div>
            ),
          renderCodeMdCodePreviewBtn,
        }}
        openApiConfig={openApiConfig}
        chatContinueApiConfig={getChatConfinueApiConfig(agentId, chatSessionId)}
        codePanelProps={{
          renderCodePanelHeaderLeft() {
            return (
              <div className="tw-text-gray-12 tw-text-md tw-font-semibold tw-flex tw-items-center">
                数据看版预览
                <span className="tw-ml-[10px] tw-text-gray-0958a tw-text-xs tw-font-normal">
                  你可于以下预览看版，如需修改可继续和 AI 对话，若编辑完毕可保存看版
                </span>
              </div>
            );
          },
          renderCodePanelHeaderRight: ({ code: codeText, codeStatus, chatStatus }) => (codeStatus === 'finish' || chatStatus === 'stop') && (
            <Flex gap={8}>
              <Tooltip
                title="我要反馈"
                OverlayTriggerProps={{
                  placement: 'top',
                }}
              >
                <Button icon={<AdqIcon name="edit" />} displayType="text" onClick={triggerFeedback}></Button>
              </Tooltip>
              <QrPreview
                userInfo={account}
                codeText={codeText}
                chatSessionId={chatSessionId || ''}
                enableReportAnalyze={!!enableReportAnalyze}
              />
            </Flex>
          ),
          codeStreamVisualize,
          onCodeRenderError,
          showCodePanelOnMount: true,
        }}
        onMount={({ ask }) => {
          askRef.current = ask;
          if (!initialPrompt) return;
          if (chatSessionIdFromProps) {
            initialAskDoneRef.current = true;
            ask({ prompt: initialPrompt, file_list: buildInitialFileList(fileUrlList) });
          }
        }}
      />
      {feedbackWidget}
    </>
  );
});

export default ChatInstance;
